import { Icon } from "@deligoo/ui"
import clsx from "clsx"
import dayjs from "dayjs"
import { memo, useCallback, useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import { batch, shallowEqual, useDispatch } from "react-redux"
import { NavLink, NavLinkProps, useHistory, useLocation } from "react-router-dom"

import { MapContext, useAppSelector } from "@/App"
import { Sidenav } from "@/components/Sidenav"
import { setDate } from "@/store/global"
import { selectCurrentTeam } from "@/store/teams"
import { resetToInitialState, setTeamsModalVisibility } from "@/store/ui"
import { getDateLabel } from "@/utils"

import cls from "./Header.module.scss"
import { OrdersSearch } from "./OrdersSearch"
import { SelectDateModal } from "./SelectDateModal"
import { SelectTeamModal } from "./SelectTeamModal"

const Link = memo((props: React.PropsWithoutRef<NavLinkProps> & React.RefAttributes<HTMLAnchorElement>) => (
  <NavLink {...props} />
))

export const MainHeader = memo(() => {
  const date = useAppSelector((state) => state.global.date)
  const team = useAppSelector(selectCurrentTeam, shallowEqual)
  const isTeamsModalOpen = useAppSelector((state) => state.ui.isTeamsModalOpen)

  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()

  const [isSidenavOpen, openSidenav] = useState(false)
  const handleSidenavClose = useCallback(() => openSidenav(false), [])

  const [isOrdersSearchActive, setOrdersSearchActive] = useState(false)
  const handleOrdersSearchClose = useCallback(() => setOrdersSearchActive(false), [])

  const [isDateModalOpen, setDateModalOpen] = useState(false)
  const handleDateModalClose = useCallback(() => setDateModalOpen(false), [])
  const handleTeamModalClose = useCallback(() => dispatch(setTeamsModalVisibility(false)), [dispatch])

  const mapRef = useContext(MapContext)
  const cityBounds = useAppSelector((state) => state.teams.cityBounds)

  const { t } = useTranslation()

  const isEventsView = location.pathname.includes("/centrum-zdarzen")
  const isEventsStatisticsView = location.pathname.includes("/statystyki")
  const isEventsListView = location.pathname.includes("/list")

  function resetModalDetailsAndMapFocus() {
    batch(() => {
      dispatch(resetToInitialState())
      if (!dayjs(date).isToday()) dispatch(setDate(dayjs().format("YYYY-MM-DD")))
    })

    if (cityBounds && mapRef?.current) {
      mapRef.current.leafletElement.flyToBounds(cityBounds, {
        maxZoom: 12,
        animate: false,
      })
    }
  }

  return (
    <>
      <Sidenav active={isSidenavOpen} handleClose={handleSidenavClose} />

      <header className={cls.header}>
        <div className={cls.start}>
          <button onClick={() => openSidenav(true)} className={cls.toggler}>
            <Icon name="hamburger" size="medium" />
          </button>

          <ul className={cls.toggleList}>
            <li className={cls.item}>
              <Link to="/" activeClassName={cls.active} className={cls.link} exact>
                <Icon name="map-view" className={cls.icon} />
                <span className={cls.label}>{t("map_view")}</span>
              </Link>
            </li>
            <li className={cls.item}>
              <Link
                to="/lista"
                activeClassName={cls.active}
                className={cls.link}
                onClick={() => resetModalDetailsAndMapFocus()}
              >
                <Icon name="list-3" className={cls.icon} />
                <span className={cls.label}>{t("list")}</span>
              </Link>
            </li>
            <li className={cls.item}>
              <Link
                to="/centrum-zdarzen"
                activeClassName={cls.active}
                className={cls.link}
                onClick={() => resetModalDetailsAndMapFocus()}
              >
                <Icon name="event-center" className={cls.icon} />
                <span className={cls.label}>{t("events_view")}</span>
              </Link>
            </li>
            <li className={clsx(cls.item, cls.smOnly)}>
              <Link
                to="/centrum-zdarzen"
                activeClassName={cls.active}
                className={cls.link}
                onClick={() => resetModalDetailsAndMapFocus()}
              >
                <Icon name="event-center" className={cls.icon} />
                <span className={cls.label}>{t("events_view")}</span>
              </Link>
            </li>
          </ul>
        </div>

        <Link to="/" className={cls.logo} onClick={() => resetModalDetailsAndMapFocus()}>
          <img src="/assets/images/deligoo.svg" alt="DeliGoo" />
        </Link>

        <div className={cls.end}>
          <ul className={cls.list}>
            <li className={cls.item}>
              {isOrdersSearchActive && <OrdersSearch handleClose={handleOrdersSearchClose} />}

              <button
                type="button"
                className={cls.link}
                onClick={() => setOrdersSearchActive(true)}
                title="Wyszukaj zlecenie"
              >
                <Icon name="search" className={cls.icon} />
              </button>
            </li>
            <li className={cls.item}>
              <button type="button" className={cls.link} disabled={isEventsView} onClick={() => setDateModalOpen(true)}>
                <Icon name="calendar" className={cls.icon} />
                <span className={cls.label}>{t(getDateLabel(date))}</span>
              </button>
            </li>
            <li className={cls.item}>
              <button onClick={() => dispatch(setTeamsModalVisibility(true))} className={cls.link} type="button">
                <Icon name="team" className={cls.icon} />
                <span className={cls.label}>{team ? team.name : "Wybierz team"}</span>
              </button>
            </li>
            <li className={cls.item}>
              <button onClick={() => history.push("/nowe-zlecenie-manualne")} className={cls.link} type="button">
                <span className={cls.label}>+ Dodaj zlecenie</span>
              </button>
            </li>
          </ul>
        </div>
      </header>

      <div className={clsx(cls.mobileNav, (isEventsStatisticsView || isEventsListView) && "d-none")}>
        <ul className={cls.list}>
          {!isEventsView && (
            <li className={cls.item}>
              <button type="button" className={cls.link} disabled={isEventsView} onClick={() => setDateModalOpen(true)}>
                <Icon name="calendar" className={cls.icon} />
                <span className={cls.label}>{t(getDateLabel(date))}</span>
              </button>
            </li>
          )}
          <li className={cls.item}>
            <button onClick={() => dispatch(setTeamsModalVisibility(true))} className={cls.link} type="button">
              <Icon name="team" className={cls.icon} />
              <span className={cls.label}>{team ? team.name : "Wybierz team"}</span>
            </button>
          </li>
        </ul>
      </div>

      {isDateModalOpen && <SelectDateModal handleClose={handleDateModalClose} />}
      {isTeamsModalOpen && <SelectTeamModal handleClose={handleTeamModalClose} />}
    </>
  )
})
